import { useState, useEffect, useRef } from "react";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { DocumentTextIcon, DocumentIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import MarkdownText from "../MarkdownText";
import DefaultIdeas from "./DefaultIdeas";
import ChatHistory from "./ChatHistory";
import {
  VendorProfileCard,
  RFIResponseCard,
  RFIComparisonCard,
  CompareVendorsAction,
  CompareVendorsWithSummary,
  BulkDownloadAction,
  RequestNewVendorLink,
  VendorListCheckAction,
} from "./ChatResponseActions";
import LogoChat from "../../assets/images/CHATDiligent.png";
import {
  useGetChatHistory,
  useGetChatMessages,
  useSendChat,
  useUpdateChatName,
} from "../../custom-hooks/queries";
import { availableChatResponseTypes } from "../../utils/chat";

function ChatDQPContent({ setOpen }) {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [messages, setMessages] = useState([]);
  const [isAiResponding, setIsAiResponding] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [archivedChats, setArchivedChats] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [chatHistory, setChatHistory] = useState([]);
  const { mutate: mutateSendChat } = useSendChat();
  const { mutate: mutateChatName } = useUpdateChatName();
  const { data: chatHistoryResponse } = useGetChatHistory();
  const { data: chatMessages } = useGetChatMessages({ sessionId: currentChatId });
  useEffect(() => {
    setChatHistory(
      Array.isArray(chatHistoryResponse) ? chatHistoryResponse : []
    );
  }, [chatHistoryResponse]);

  useEffect(() => {
    setMessages(chatMessages || []);
  }, [chatMessages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isAiResponding]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleNewChat = () => {
    // Reset to new chat
    setMessages([]);
    setCurrentChatId("");
    setCurrentPrompt("");
    setAttachedFiles([]);
  };

  const handleLoadChat = async (chat) => {
    // Save current chat if it has messages
    if (messages.length > 0 && currentChatId !== chat.id) {
      const firstUserMessage = messages.find((msg) => msg.role === "user");
      const chatTitle = firstUserMessage
        ? firstUserMessage.content
        : "New conversation";

      const savedChat = {
        id: currentChatId || Date.now(),
        title: chatTitle,
        createdAt: new Date().toISOString(),
        timeAgo: "Just now",
      };

      setChatHistory((prev) => {
        // Don't add if it's already in history
        const exists = prev.some((c) => c.id === savedChat.id);
        return exists ? prev : [savedChat, ...prev];
      });
    }

    setCurrentChatId(chat.id);
    setAttachedFiles([]);
  };

  const handleArchiveChat = (chatId) => {
    // Find the chat to archive
    const chatToArchive = chatHistory.find((chat) => chat.id === chatId);
    if (chatToArchive) {
      // Add to archived chats
      setArchivedChats((prev) => [chatToArchive, ...prev]);
      // Remove from active chats
      setChatHistory((prev) => prev.filter((chat) => chat.id !== chatId));

      // If the archived chat was the current one, clear the messages
      if (currentChatId === chatId) {
        setMessages([]);
        setCurrentChatId(null);
      }
    }
  };

  const handleRestoreChat = (chatId) => {
    // Find the chat to restore
    const chatToRestore = archivedChats.find((chat) => chat.id === chatId);
    if (chatToRestore) {
      // Add back to active chats
      setChatHistory((prev) => [chatToRestore, ...prev]);
      // Remove from archived chats
      setArchivedChats((prev) => prev.filter((chat) => chat.id !== chatId));
    }
  };

  const handleRenameChat = (sessionId, title) => {
    mutateChatName(
      { sessionId, title },
      {
        onSuccess: () => {
          setChatHistory((prev) =>
            prev.map((chat) =>
              chat.id === sessionId ? { ...chat, title } : chat
            )
          );
        },
      }
    );
  };

  const handleFileAttach = (e) => {
    const files = Array.from(e.target.files);

    // Allowed file types
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain",
      "text/csv",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ];

    const validFiles = files.filter((file) => allowedTypes.includes(file.type));

    if (validFiles.length !== files.length) {
      alert(
        "Some files were not added. Only documents, spreadsheets, PDFs, and text files are supported."
      );
    }

    // Limit to 5 files total
    const remainingSlots = 5 - attachedFiles.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);

    if (filesToAdd.length < validFiles.length) {
      alert("You can only attach up to 5 files at once.");
    }

    // Add files with preview data
    const newFiles = filesToAdd.map((file) => ({
      id: Date.now() + Math.random(),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    setAttachedFiles((prev) => [...prev, ...newFiles]);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (fileId) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes("pdf")) {
      return { icon: DocumentTextIcon, color: "text-red-600", bg: "bg-red-50" };
    } else if (
      fileType.includes("spreadsheet") ||
      fileType.includes("excel") ||
      fileType.includes("csv")
    ) {
      return { icon: DocumentIcon, color: "text-green-600", bg: "bg-green-50" };
    } else if (fileType.includes("word") || fileType.includes("document")) {
      return { icon: DocumentIcon, color: "text-blue-600", bg: "bg-blue-50" };
    } else if (
      fileType.includes("presentation") ||
      fileType.includes("powerpoint")
    ) {
      return {
        icon: DocumentIcon,
        color: "text-orange-600",
        bg: "bg-orange-50",
      };
    } else {
      return { icon: DocumentIcon, color: "text-gray-600", bg: "bg-gray-50" };
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const buildChatErrorText = (error) => {
    const generic =
      "Sorry, an error occurred while processing your request. Please try again later.";
    return generic;
  };

  const handleSendMessage = (messageText = null) => {
    const textToSend = messageText || currentPrompt;
    // const hasAttachments = attachedFiles.length > 0;

    // if ((!textToSend.trim() && !hasAttachments) || isAiResponding) return;
    if (!textToSend.trim() || isAiResponding) return;

    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: textToSend || "📎 Attached files",
      // attachments: hasAttachments ? [...attachedFiles] : null,
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setCurrentPrompt("");
    setAttachedFiles([]);

    // Start AI response loading
    const sessionId = currentChatId || "";
    setIsAiResponding(true);
    mutateSendChat(
      { sessionId, userPrompt: textToSend },
      {
        onSuccess: (newChatData) => {
          setMessages((prev) => [...prev, newChatData]);

          if (sessionId === "") {
            setCurrentChatId(newChatData.sessionId);
            const savedChat = {
              id: newChatData.sessionId,
              title: textToSend,
              createdAt: new Date().toISOString(),
              timeAgo: "Just now",
            };
            setChatHistory((prev) => [savedChat, ...prev]);
          }
        },
        onError: (error) => {
          console.error("Error fetching chat response:", error);
          const errText = buildChatErrorText(error);
          const errorMessage = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: errText,
            createdAt: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, errorMessage]);
        },
        onSettled: () => {
          setIsAiResponding(false);
        },
      }
    );

    // Reset textarea height
    setTimeout(() => {
      const textarea = document.querySelector("textarea");
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = "44px";
      }
    }, 0);
  };

  const handleSuggestionClick = (suggestion) => {
    // Start chat with the suggestion as the first message
    handleSendMessage(suggestion.description);
  };

  return (
    <div className="flex h-full">
      {/* Left Panel - Chat History Sidebar */}
      <div
        className={`${
          isCollapsed ? "w-12" : "w-80"
        } bg-slate-50 transition-all duration-300 border-r border-gray-200 flex-shrink-0`}
      >
        <div className="flex flex-col h-full">
          {/* Collapsed state - icon-only sidebar */}
          {isCollapsed && (
            <div className="flex flex-col items-center py-2 space-y-2">
              {/* Expand button */}
              <button
                onClick={toggleCollapse}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Expand chat history"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
              {/* New chat icon */}
              <button
                type="button"
                onClick={handleNewChat}
                disabled={messages.length === 0}
                className={`p-2 rounded-md transition-colors ${
                  messages.length > 0
                    ? "hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                    : "text-gray-300 cursor-not-allowed"
                }`}
                aria-label="New chat"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Expanded state - full sidebar */}
          {!isCollapsed && (
            <div className="flex flex-col h-full w-80">
              {/* Header with collapse button and new chat */}
              <div className="flex flex-col p-2 space-y-2 border-b border-gray-200">
                <button
                  onClick={toggleCollapse}
                  className="p-2 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors self-start"
                  aria-label="Collapse chat history"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={handleNewChat}
                  disabled={messages.length === 0}
                  className={`border p-2 w-full rounded-md text-left flex items-center transition-all shadow-sm ${
                    messages.length > 0
                      ? "border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-[#6036C4] hover:shadow-md focus:outline-none focus:border-[#6036C4] focus:ring-2 focus:ring-[#6036C4] focus:ring-opacity-20"
                      : "border-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  <PlusIcon
                    className={`h-5 w-5 mr-2 ${
                      messages.length > 0 ? "text-gray-500" : "text-gray-400"
                    }`}
                  />
                  <span
                    className={`text-sm font-normal ${
                      messages.length > 0 ? "text-gray-600" : "text-gray-400"
                    }`}
                  >
                    New chat
                  </span>
                </button>
              </div>

              {/* Chat history content */}
              <div className="flex-1 overflow-y-auto p-4">
                <ChatHistory
                  savedChats={chatHistory}
                  archivedChats={archivedChats}
                  onChatClick={handleLoadChat}
                  currentChatId={currentChatId}
                  onArchiveChat={handleArchiveChat}
                  onRestoreChat={handleRestoreChat}
                  onRenameChat={handleRenameChat}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Main Chat Area */}
      <div className="flex-1 flex flex-col min-h-screen bg-white relative transition-all duration-300">
        {/* Header */}
        <header className="flex justify-between items-center p-4 border-b border-gray-200">
          <h1 className="text-lg font-semibold text-gray-900">&nbsp;</h1>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="relative rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <span className="absolute -inset-2.5" />
            <span className="sr-only">Close panel</span>
            <XMarkIcon aria-hidden="true" className="size-6" />
          </button>
        </header>

        {/* Main content - Chat Messages */}
        <main className="flex-grow overflow-y-auto p-4 bg-white">
          <div className="max-w-4xl mx-auto">
            {messages.length === 0 ? (
              // Empty state - show logo
              <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                <div className="h-24">
                  <img
                    src={LogoChat}
                    className="h-full w-auto object-contain"
                    alt="ChatDiligent"
                  />
                </div>
              </div>
            ) : (
              // Messages display
              <div className="space-y-6 py-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === "user"
                          ? "text-white"
                          : "bg-gray-100 text-gray-900"
                      }`}
                      style={
                        message.role === "user"
                          ? { backgroundColor: "#6036C4" }
                          : {}
                      }
                    >
                      {/* File Attachments */}
                      {message.attachments &&
                        message.attachments.length > 0 && (
                          <div className="mb-2 space-y-2">
                            {message.attachments.map((attachment) => {
                              const {
                                icon: FileIcon,
                                color,
                                bg,
                              } = getFileIcon(attachment.type);
                              return (
                                <div
                                  key={attachment.id}
                                  className={`flex items-center gap-2 p-2 rounded-lg ${
                                    message.role === "user"
                                      ? "bg-white bg-opacity-20"
                                      : "bg-white border border-gray-200"
                                  }`}
                                >
                                  <div
                                    className={`flex-shrink-0 p-2 rounded ${
                                      message.role === "user"
                                        ? "bg-white bg-opacity-30"
                                        : bg
                                    }`}
                                  >
                                    <FileIcon
                                      className={`h-5 w-5 ${
                                        message.role === "user"
                                          ? "text-white"
                                          : color
                                      }`}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p
                                      className={`text-xs font-medium truncate ${
                                        message.role === "user"
                                          ? "text-white"
                                          : "text-gray-900"
                                      }`}
                                    >
                                      {attachment.name}
                                    </p>
                                    <p
                                      className={`text-xs ${
                                        message.role === "user"
                                          ? "text-white text-opacity-75"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      {formatFileSize(attachment.size)}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                      {/* Message Content */}
                      {message.content && (
                        <>
                          {message.role === "assistant" ? (
                            <MarkdownText text={message.content} />
                          ) : (
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">
                              {message.content}
                            </p>
                          )}
                        </>
                      )}

                      {/* Action Components */}
                      {message.actionType && message.role === "assistant" && (
                        <div className="mt-3 space-y-2">
                          {message.actionType ===
                            availableChatResponseTypes.vendor_profile_discovery_card && (
                            <>
                              {/* Total Results */}
                              {message.totalResults && (
                                <p className="text-sm font-medium text-gray-600 mb-3">
                                  {message.totalResults}
                                </p>
                              )}

                              {message.actionData?.map((vendor, idx) => (
                                <VendorProfileCard
                                  key={idx}
                                  vendor={vendor}
                                  highlightTerms={message.highlightTerms || []}
                                  badgeType={
                                    message.badgeType || "therapeuticAreas"
                                  }
                                  onClose={
                                    setOpen ? () => setOpen(false) : undefined
                                  }
                                />
                              ))}

                              {/* Other relevant vendors */}
                              {message.otherRelevantVendors &&
                                message.otherRelevantVendors.length > 0 && (
                                  <div className="mt-4">
                                    <p className="text-sm font-medium text-gray-700 mb-2">
                                      Other vendors:
                                    </p>
                                    <ul className="space-y-1.5">
                                      {message.otherRelevantVendors.map(
                                        (vendor, idx) => {
                                          const handleVendorClick = () => {
                                            if (setOpen) setOpen(false);
                                            setTimeout(() => {
                                              navigate(
                                                `/provider/${vendor.id}/overview`
                                              );
                                              window.scrollTo(0, 0);
                                            }, 150);
                                          };

                                          return (
                                            <li
                                              key={idx}
                                              className="text-sm text-gray-600"
                                            >
                                              <button
                                                onClick={handleVendorClick}
                                                className="font-medium text-[#6036C4] hover:text-[#5028b0] hover:underline transition-colors"
                                              >
                                                {vendor.name}
                                              </button>
                                              {" — "}
                                              {vendor.reason}
                                            </li>
                                          );
                                        }
                                      )}
                                    </ul>
                                  </div>
                                )}

                              {/* Download Excel Button */}
                              {message.excelFileBase64 && message.excelFileName && (
                                <button
                                  onClick={() => {
                                    try {
                                      // Decode base64 to binary
                                      const binaryString = atob(message.excelFileBase64);
                                      const bytes = new Uint8Array(binaryString.length);
                                      for (let i = 0; i < binaryString.length; i++) {
                                        bytes[i] = binaryString.charCodeAt(i);
                                      }
                                      
                                      // Create blob and download
                                      const blob = new Blob([bytes], {
                                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                                      });
                                      const url = URL.createObjectURL(blob);
                                      const link = document.createElement("a");
                                      link.href = url;
                                      link.download = message.excelFileName;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      URL.revokeObjectURL(url);
                                    } catch (error) {
                                      console.error("Error downloading Excel file:", error);
                                    }
                                  }}
                                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors shadow-sm"
                                  style={{ backgroundColor: 'rgb(96, 54, 196)' }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgb(80, 40, 176)'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgb(96, 54, 196)'}
                                >
                                  <DocumentArrowDownIcon className="h-5 w-5" />
                                  Download Excel
                                </button>
                              )}
                            </>
                          )}

                          {message.actionType === "rfiResponses" &&
                            message.actionData?.map((rfi, idx) => (
                              <RFIResponseCard
                                key={idx}
                                vendor={rfi.vendor}
                                rfiTitle={rfi.rfiTitle}
                                onClose={
                                  setOpen ? () => setOpen(false) : undefined
                                }
                              />
                            ))}

                          {message.actionType ===
                            availableChatResponseTypes.rfi_comparison_card && (
                            <RFIComparisonCard
                              templateName={message.actionData.templateName}
                              vendors={message.actionData.vendors}
                              question={message.actionData.question}
                              responses={message.actionData.responses}
                              summary={message.actionData.summary}
                              onClose={
                                setOpen ? () => setOpen(false) : undefined
                              }
                            />
                          )}

                          {message.actionType === "compareVendors" && (
                            <CompareVendorsAction
                              vendors={message.actionData.vendors}
                              therapeuticArea={
                                message.actionData.therapeuticArea
                              }
                              indication={message.actionData.indication}
                              onClose={
                                setOpen ? () => setOpen(false) : undefined
                              }
                            />
                          )}

                          {message.actionType ===
                            availableChatResponseTypes.trial_experience_card && (
                            <CompareVendorsWithSummary
                              title={message.actionData.title}
                              summary={message.actionData.summary}
                              vendors={message.actionData.vendors}
                              comparison={message.actionData.comparison}
                              onClose={
                                setOpen ? () => setOpen(false) : undefined
                              }
                            />
                          )}

                          {message.actionType ===
                            availableChatResponseTypes.rfi_download_card && (
                            <BulkDownloadAction
                              rfis={message.actionData.rfis}
                              onClose={
                                setOpen ? () => setOpen(false) : undefined
                              }
                            />
                          )}

                          {message.actionType === "requestVendor" && (
                            <RequestNewVendorLink
                              vendorName={message.actionData.vendorName}
                              websiteUrl={message.actionData.websiteUrl}
                              onClose={
                                setOpen ? () => setOpen(false) : undefined
                              }
                            />
                          )}

                          {message.actionType === "vendorListCheck" && (
                            <VendorListCheckAction
                              existingVendors={
                                message.actionData.existingVendors
                              }
                              missingVendors={message.actionData.missingVendors}
                              onClose={
                                setOpen ? () => setOpen(false) : undefined
                              }
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* AI Loading Animation */}
                {isAiResponding && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-gray-100">
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          <div
                            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                            style={{
                              animationDelay: "0ms",
                              animationDuration: "1000ms",
                            }}
                          ></div>
                          <div
                            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                            style={{
                              animationDelay: "150ms",
                              animationDuration: "1000ms",
                            }}
                          ></div>
                          <div
                            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                            style={{
                              animationDelay: "300ms",
                              animationDuration: "1000ms",
                            }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500 ml-2">
                          ChatDiligent is thinking...
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Scroll anchor */}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white">
          {/* Suggestion chips - only show when no messages */}
          {messages.length === 0 && (
            <div className="py-6">
              <DefaultIdeas onSuggestionClick={handleSuggestionClick} />
            </div>
          )}

          {/* Modern AI-style input area */}
          <div className="p-4">
            <div className="max-w-4xl mx-auto">
              {/* File Attachments Preview */}
              {attachedFiles.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {attachedFiles.map((file) => {
                    const {
                      icon: FileIcon,
                      color,
                      bg,
                    } = getFileIcon(file.type);
                    return (
                      <div
                        key={file.id}
                        className="flex items-center gap-2 p-2 pr-3 bg-white border border-gray-300 rounded-lg shadow-sm"
                      >
                        <div className={`flex-shrink-0 p-1.5 rounded ${bg}`}>
                          <FileIcon className={`h-4 w-4 ${color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate max-w-[150px]">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveFile(file.id)}
                          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label="Remove file"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="relative flex items-center gap-1 bg-white border border-gray-300 rounded-2xl shadow-sm hover:shadow-md transition-shadow focus-within:border-[#6036C4] focus-within:ring-2 focus-within:ring-[#6036C4] focus-within:ring-opacity-20 p-2">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx"
                  onChange={handleFileAttach}
                  className="hidden"
                />

                {/* Attachment button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachedFiles.length >= 5 || isAiResponding}
                  className={`flex-shrink-0 p-2 rounded-lg transition-colors self-center ${
                    attachedFiles.length >= 5 || isAiResponding
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  }`}
                  aria-label="Attach files"
                >
                  <PaperClipIcon className="h-5 w-5" />
                </button>

                <textarea
                  className="flex-1 resize-none border-0 bg-transparent px-2 py-2.5 text-gray-900 font-normal placeholder:text-gray-400 placeholder:font-light focus:outline-none focus:ring-0 max-h-32 min-h-[44px]"
                  placeholder="Ask ChatDiligent..."
                  value={currentPrompt}
                  onChange={(e) => setCurrentPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  rows={1}
                  style={{
                    height: "auto",
                    minHeight: "44px",
                  }}
                  onInput={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height =
                      Math.min(e.target.scrollHeight, 128) + "px";
                  }}
                  disabled={isAiResponding}
                />
                <button
                  type="button"
                  onClick={() => handleSendMessage()}
                  disabled={
                    (!currentPrompt.trim() && attachedFiles.length === 0) ||
                    isAiResponding
                  }
                  className={`flex-shrink-0 rounded-lg p-2 transition-all text-white shadow-sm self-center ${
                    (currentPrompt.trim() || attachedFiles.length > 0) &&
                    !isAiResponding
                      ? ""
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                  style={
                    (currentPrompt.trim() || attachedFiles.length > 0) &&
                    !isAiResponding
                      ? { backgroundColor: "#6036C4" }
                      : {}
                  }
                  onMouseEnter={(e) => {
                    if (
                      (currentPrompt.trim() || attachedFiles.length > 0) &&
                      !isAiResponding
                    ) {
                      e.currentTarget.style.backgroundColor = "#5028b0";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      (currentPrompt.trim() || attachedFiles.length > 0) &&
                      !isAiResponding
                    ) {
                      e.currentTarget.style.backgroundColor = "#6036C4";
                    }
                  }}
                  aria-label="Send message"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-2 text-xs text-center text-gray-500">
                ChatDiligent can make mistakes. Please verify important
                information.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default ChatDQPContent;
